import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { resolveApiMessage, showToast } from '../../../helpers';
import {
  setOpsAssignmentDuration,
  TOpsClassRelatedEx,
} from '../../../requests/opsRegistrationsExtra';
import { btnGhost, btnSecondary, Card, inputClass } from '../ui';

/** Who teaches the class, with the hours COGS reads. */
const SmeAssignmentsCard: React.FC<{
  id: string;
  related: TOpsClassRelatedEx;
}> = ({ id, related }) => {
  const queryClient = useQueryClient();
  // "Adjust course duration" on an SME assignment (the weekly schedule
  // audit's correction — COGS reads the hours)
  const [durationEdit, setDurationEdit] = useState<{
    id: string;
    hours: string;
    note: string;
  } | null>(null);
  const saveDuration = useMutation({
    mutationFn: () =>
      setOpsAssignmentDuration(id!, durationEdit!.id, {
        hours: Number(durationEdit!.hours),
        note: durationEdit!.note.trim() || undefined,
      }),
    onSuccess: (r) => {
      showToast(
        r.previous == null
          ? `Course duration set to ${r.duration} h`
          : `Course duration ${r.previous} h → ${r.duration} h`,
        'success',
      );
      setDurationEdit(null);
      queryClient.invalidateQueries({ queryKey: ['opsClassRelated', id] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  return (
    <Card
      title={`SME Assignment · ${related.assignments.length}`}
      right={
        (related as { cogsTotal?: number }).cogsTotal != null ? (
          <span className="text-xs text-gray-bold [font-variant-numeric:tabular-nums]">
            Course cost $
            {Number(
              (related as { cogsTotal?: number }).cogsTotal,
            ).toLocaleString()}
          </span>
        ) : null
      }
    >
      <div className="space-y-1.5 p-5 text-sm">
        {related.assignments.length === 0 ? (
          <span className="text-gray-two">none</span>
        ) : (
          related.assignments.map((a) => (
            <div key={a.id}>
              <div className="flex flex-wrap items-center gap-x-2">
                <b className="text-gray-four">{a.instructor}</b>
                <span className="text-xs text-gray-bold">
                  {[a.role, a.contractType, a.status]
                    .filter(Boolean)
                    .map((x) => `· ${x}`)
                    .join(' ')}
                </span>
                <span className="text-xs text-gray-five [font-variant-numeric:tabular-nums]">
                  · {a.duration != null ? `${a.duration} h` : 'no hours'}
                  {a.adjustedDuration != null &&
                    a.adjustedDuration !== a.duration &&
                    ` (adjusted ${a.adjustedDuration} h)`}
                </span>
                {durationEdit?.id !== a.id && (
                  <button
                    type="button"
                    className={`${btnGhost} text-xs`}
                    onClick={() =>
                      setDurationEdit({
                        id: a.id,
                        hours: a.duration != null ? String(a.duration) : '',
                        note: '',
                      })
                    }
                  >
                    Adjust course duration
                  </button>
                )}
              </div>
              {durationEdit?.id === a.id && (
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    className={`${inputClass} w-24`}
                    value={durationEdit.hours}
                    onChange={(e) =>
                      setDurationEdit({
                        ...durationEdit,
                        hours: e.target.value,
                      })
                    }
                  />
                  <span className="text-xs text-gray-bold">hours</span>
                  <input
                    className={`${inputClass} w-56`}
                    placeholder="Why — recorded with your name"
                    value={durationEdit.note}
                    onChange={(e) =>
                      setDurationEdit({
                        ...durationEdit,
                        note: e.target.value,
                      })
                    }
                  />
                  <button
                    type="button"
                    className={btnSecondary}
                    disabled={
                      saveDuration.isPending ||
                      durationEdit.hours === '' ||
                      Number(durationEdit.hours) < 0
                    }
                    onClick={() => saveDuration.mutate()}
                  >
                    {saveDuration.isPending ? 'Saving…' : 'Save hours'}
                  </button>
                  <button
                    type="button"
                    className={btnGhost}
                    onClick={() => setDurationEdit(null)}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </Card>
  );
};

export default SmeAssignmentsCard;
